import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { submitContact } from "@/lib/api";

const INTERESTS = [
  "Foundation Track course",
  "Workshops & training",
  "NGO / community collaboration",
  "Corporate wellbeing",
  "Press / media",
  "Something else",
];

export default function ContactForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interest: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.name.trim()) return "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      return "Please enter a valid email.";
    if (form.message.trim().length < 10)
      return "A short message (10+ characters) helps us reply better.";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, interest: form.interest || null, phone: form.phone || null };
      const res = await submitContact(payload);
      toast.success(res.message || "Thanks for reaching out!");
      setSubmitted(true);
      setForm({ name: "", email: "", phone: "", interest: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div
        data-testid="contact-form-success"
        className="bg-paper rounded-2xl border border-[rgba(126,155,134,0.35)] p-8"
      >
        <div className="text-[11px] uppercase tracking-[0.22em] text-moss font-semibold">
          Message received
        </div>
        <h3 className="mt-2 font-display text-2xl text-ink">
          Thank you for reaching out.
        </h3>
        <p className="mt-3 text-ink-muted leading-[1.75]">
          We read every message ourselves. Expect a thoughtful reply within 2–3 working days.
          If it's urgent, ping us on WhatsApp — we're usually quicker there.
        </p>
        <Button
          type="button"
          onClick={() => setSubmitted(false)}
          variant="outline"
          data-testid="contact-form-reset-button"
          className="mt-5 border-[rgba(74,63,57,0.18)] hover:bg-[rgba(200,106,74,0.08)]"
        >
          Send another message
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="contact-form"
      className="bg-paper rounded-2xl border border-[rgba(74,63,57,0.10)] p-6 sm:p-8 space-y-5"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label htmlFor="name" className="text-sm text-ink">Name *</Label>
          <Input
            id="name"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            data-testid="contact-form-name-input"
            placeholder="Your full name"
            className="bg-cream/40 border-[rgba(74,63,57,0.18)]"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm text-ink">Email *</Label>
          <Input
            id="email"
            type="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            data-testid="contact-form-email-input"
            placeholder="you@example.com"
            className="bg-cream/40 border-[rgba(74,63,57,0.18)]"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label htmlFor="phone" className="text-sm text-ink">Phone <span className="text-ink-muted">(optional)</span></Label>
          <Input
            id="phone"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            data-testid="contact-form-phone-input"
            placeholder="+91 ..."
            className="bg-cream/40 border-[rgba(74,63,57,0.18)]"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-sm text-ink">I'm reaching out about</Label>
          <Select value={form.interest} onValueChange={(v) => update("interest", v)}>
            <SelectTrigger
              data-testid="contact-form-interest-select"
              className="bg-cream/40 border-[rgba(74,63,57,0.18)]"
            >
              <SelectValue placeholder="Choose a topic" />
            </SelectTrigger>
            <SelectContent>
              {INTERESTS.map((i) => (
                <SelectItem key={i} value={i} data-testid={`interest-option-${i.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  {i}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="message" className="text-sm text-ink">Your message *</Label>
        <Textarea
          id="message"
          rows={6}
          value={form.message}
          onChange={(e) => update("message", e.target.value)}
          data-testid="contact-form-message-textarea"
          placeholder="Tell us a little about what you're looking for, or what you'd like to explore together."
          className="bg-cream/40 border-[rgba(74,63,57,0.18)] resize-y"
        />
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-1">
        <p className="text-xs text-ink-muted max-w-md">
          We treat your details with care. We'll only use them to reply and never share with third parties.
        </p>
        <Button
          type="submit"
          disabled={loading}
          data-testid="contact-form-submit-button"
          className="bg-[#C86A4A] hover:bg-[#B85F41] text-white rounded-xl px-6 transition-colors"
        >
          {loading ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Sending…
            </span>
          ) : (
            "Send message"
          )}
        </Button>
      </div>
    </form>
  );
}
